<script lang="ts" setup>
import CommunityButton from '@/components/common/buttons/CommunityButton.vue'
import CommunityDrawerWrapper from '@/components/common/drawers/CommunityDrawerWrapper.vue'
import { app } from '@/composition/app'
import { GroupState } from '@/composition/groups'
import { UserState } from '@/composition/users'
import { DEFAULT_GROUP_LOGO } from '@/helper/const'
import { AnswerType } from '@/models/Forms'
import { MemberStatus } from '@/models/Groups'
import { FormsService } from '@/service/FormsService'
import { XCloseIcon } from '@gohighlevel/ghl-icons/24/outline'
import {
  UIModal,
  UIModalHeader,
  UISpin,
  UITextLgMedium,
} from '@gohighlevel/ghl-ui'
import { NDrawerContent } from 'naive-ui'
import { nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import MembershipQuestionsBase from '../membershipQuestions/MembershipQuestionsBase.vue'

const props = defineProps({
  show: Boolean,
  membershipAnswers: Object as any,
  contactId: { type: String, required: false },
  fullName: { type: String, required: true },
  membershipRequestCallback: {
    type: Function,
    required: true,
  },
  showActionFooter: {
    type: Boolean,
    required: false,
    default: false,
  },
})
const isLoading = ref(true)
const formQuestions = ref()
const { t } = useI18n()
const emit = defineEmits(['update:confirmed', 'update:show'])
async function closeModal() {
  emit('update:show', false)
}

function initializeMembershipAnswers() {
  return formQuestions.value.map((question, questionIndex) => {
    if (question.answerType == AnswerType.MULTIPLE_SELECT) {
      const answerOptions = formQuestions.value[
        questionIndex
      ]?.answerOptions.map((_answerOption, answerOptionIndex) => {
        return props.membershipAnswers.answers[questionIndex][answerOptionIndex]
      })

      return answerOptions
    } else {
      return props.membershipAnswers.answers[questionIndex]
    }
  })
}

const isApprovingRequest = ref(false)
const isDecliningRequest = ref(false)
const membershipAnswers = ref()
async function handleDeclineRequest() {
  isDecliningRequest.value = true
  await UserState.handleRemoveOrDeclineRequest(
    props.contactId,
    MemberStatus.ACTIVE,
    true
  )
  isDecliningRequest.value = false
  props.membershipRequestCallback()
  emit('update:show', false)
}

async function handleApproveRequest() {
  isApprovingRequest.value = true
  await UserState.handleApproveRequest(props.contactId)
  isApprovingRequest.value = false
  props.membershipRequestCallback()
  emit('update:show', false)
}
watch(
  () => props.show,
  async () => {
    console.log('showActionFooter :: ', props.showActionFooter)
    isLoading.value = true
    console.log('props.membershipAnswers :: ', props?.membershipAnswers)
    const formId = props?.membershipAnswers?.formId
    console.log('formId :: ', formId)
    formQuestions.value = (await FormsService.findById(formId)).questions
    membershipAnswers.value = initializeMembershipAnswers()
    isLoading.value = false
  }
)
</script>
<template>
  <UIModal
    :show="show && !app.state.isMobile"
    class-name="membership-question-modal  left-0 right-0 top-0 flex h-full justify-center rounded-none  overflow-hidden   sm:top-auto md:h-auto md:rounded-xl 2xl:top-auto sm:max-w-md max-w-full z-50 p-0"
    @update:show="closeModal"
  >
    <template #header>
      <UIModalHeader id="modal-header" type="custom" @close="closeModal">
        <template #customHeader />
      </UIModalHeader>
      <UISpin
        v-if="isLoading"
        class="flex min-h-80 items-center justify-center"
      />
      <div
        v-else
        class="max-h-full divide-x-0 divide-y divide-solid divide-communities-border"
      >
        <div class="flex flex-col p-4 md:px-6">
          <div class="flex w-full justify-between">
            <div class="h-12 w-12">
              <img
                id="logo_plus_icon"
                :src="
                  GroupState.state.groupData.branding?.logo ||
                  DEFAULT_GROUP_LOGO
                "
                :alt="t('common.ariaLabels.logo')"
                class="h-full w-full rounded-md border-[1px] border-solid border-communities-border shadow-lg"
              />
            </div>
            <UITextLgMedium
              class="text-communities-font-primary md:w-2/3 md:text-center md:text-lg"
              >{{ t('groupInfo.answeredQuestions') }}
              <div class="text-sm font-normal">
                <span class="font-medium text-communities-font-primary">{{
                  fullName
                }}</span>
                {{ t('groupInfo.answeredQuestionsMessage') }}
                {{ GroupState?.state?.groupData?.name }}
              </div>
            </UITextLgMedium>
            <div
              id="close-review-membership-answers-modal"
              tabindex="0"
              role="button"
              :aria-label="t('common.ariaLabels.closeReviewMembershipAnswersModal')"
              @click="closeModal"
              @keyup.enter="closeModal"
            >
              <XCloseIcon class="my-2 h-6 text-communities-font-secondary" />
            </div>
          </div>
        </div>
        <div
          class="member-question-scrollbar member-question-scrollbar overflow-auto px-4 md:max-h-[400px] md:px-6"
        >
          <MembershipQuestionsBase
            v-model:answers="membershipAnswers"
            :questions="formQuestions"
            :is-read-only="membershipAnswers"
          />
        </div>
        <div
          v-if="showActionFooter"
          class="flex justify-between gap-2 p-4 sm:p-6"
        >
          <CommunityButton
            id="decline-membership-answers"
            :disabled="isDecliningRequest"
            :loading="isDecliningRequest"
            class="w-[11rem]"
            button-type="tertiary"
            @click="handleDeclineRequest"
          >
            {{ t('common.decline') }}</CommunityButton
          >
          <CommunityButton
            id="approve-membership-answers"
            button-type="primary"
            :disabled="isApprovingRequest"
            :loading="isApprovingRequest"
            class="w-[11rem]"
            @click="handleApproveRequest"
          >
            {{ t('common.approve') }}
          </CommunityButton>
        </div>
      </div>
    </template>
  </UIModal>
  <CommunityDrawerWrapper
    v-if="show && app.state.isMobile"
    id="review-membership-answers-drawer"
    :show="show"
    class="rounded-t-xl p-0"
    :placement="'bottom'"
    :z-index="2003"
    :height="600"
    @update:show="closeModal"
  >
    <NDrawerContent
      id="post-options-drawer-menu"
      v-swipe="{
        direction: 'down',
        callback: () => {
          nextTick(() => closeModal())
        },
      }"
      class="flex flex-row justify-center"
      :closable="false"
    >
      <template #header>
        <div
          class="mx-auto mb-2 h-1 w-16 rounded-md border border-solid border-communities-border bg-communities-border"
        />
        <div class="flex flex-col p-1">
          <div class="flex w-full justify-between">
            <div class="h-12 w-24 pr-2">
              <img
                id="logo_plus_icon"
                :src="
                  GroupState.state.groupData.branding?.logo ||
                  DEFAULT_GROUP_LOGO
                "
                :alt="t('common.ariaLabels.logo')"
                class="h-full w-full rounded-md border-[1px] border-solid border-communities-border shadow-lg"
              />
            </div>
            <UITextLgMedium
              class="text-communities-font-primary md:w-2/3 md:text-center md:text-lg"
              >{{ t('groupInfo.answeredQuestions') }}
              <div class="text-sm font-normal">
                <span class="font-medium text-communities-font-primary">{{
                  fullName
                }}</span>
                {{ t('groupInfo.answeredQuestionsMessage') }}
                {{ GroupState?.state?.groupData?.name }}
              </div>
            </UITextLgMedium>
          </div>
        </div>
      </template>
      <UISpin
        v-if="isLoading"
        class="flex min-h-80 items-center justify-center"
      />
      <div
        v-else
        class="max-h-full divide-x-0 divide-y divide-solid divide-communities-border overflow-y-scroll"
      >
        <div class="px-2 py-1">
          <MembershipQuestionsBase
            v-model:answers="membershipAnswers"
            :questions="formQuestions"
            :is-read-only="membershipAnswers"
          />
        </div>
      </div>
      <template #footer>
        <div v-if="showActionFooter" class="flex w-full gap-2">
          <CommunityButton
            id="decline-membership-answers"
            :disabled="isDecliningRequest"
            :loading="isDecliningRequest"
            class="flex-1"
            button-type="tertiary"
            @click="handleDeclineRequest"
          >
            {{ t('common.decline') }}</CommunityButton
          >
          <CommunityButton
            id="approve-membership-answers"
            button-type="primary"
            :disabled="isApprovingRequest"
            :loading="isApprovingRequest"
            class="flex-1"
            @click="handleApproveRequest"
          >
            {{ t('common.approve') }}
          </CommunityButton>
        </div>
      </template>
    </NDrawerContent>
  </CommunityDrawerWrapper>
</template>
<style>
.membership-question-modal {
  min-width: 350px;
  min-height: 250px;
}
.member-question-scrollbar {
  scrollbar-width: thin;
}

.member-question-scrollbar::-webkit-scrollbar {
  width: 0px;
}

.member-question-scrollbar:hover::-webkit-scrollbar {
  width: 0px;
}

.member-question-scrollbar::-webkit-scrollbar-thumb {
  background-color: #c2c2c2;
  border-radius: 2px;
}
</style>
